import { useEffect, useRef, useState } from "react";
import {
  useLive,
  get,
  post,
  del,
  formatPhone,
  ago,
  type LeadDetail,
  type Message,
  type AgentTurn,
} from "../lib/api";
import {
  Card,
  Score,
  Pill,
  Button,
  ControlPill,
  StagePill,
  FIELD_GROUPS,
  FIELD_LABEL,
  formatFact,
  Carregando,
  Avatar,
  Segmentos,
} from "../lib/ui";
import {
  ArrowLeft,
  UserCheck,
  Bot,
  Trash2,
  SendHorizontal,
  MessageSquare,
  Sparkles,
  ClipboardList,
  Calculator,
  History,
  FileText,
  ChevronDown,
  ChevronRight,
  Clock,
  CircleAlert,
  Check,
  CheckCheck,
  Paperclip,
  Copy,
  X,
} from "lucide-react";
import { Rastro } from "./Rastro";
import type { FormularioRecebido } from "../lib/api";

/**
 * A ficha do lead: conversa a esquerda, o que sabemos a direita.
 *
 * A parte que importa e a coluna da direita mostrar a PROCEDENCIA de cada
 * fato. "Entrada: R$ 80.000" sozinho e um numero que o corretor nao sabe se
 * pode usar; "declarado" ao lado dele e o que permite ele abrir a conversa
 * com confianca.
 */
export function Lead({ leadId, onBack }: { leadId: string; onBack: () => void }) {
  const { data, reload } = useLive<LeadDetail>(`/leads/${leadId}`, [
    "messages",
    "leads",
    "conversations",
    "handoffs",
  ]);
  /*
   * Os turnos sobem para ca porque servem a duas coisas: o rastro e a linha de
   * detalhe embaixo de cada resposta da IA. Duas buscas do mesmo dado seriam
   * dois streams para o mesmo fato -- e duas telas podendo discordar.
   */
  const { data: turns, reload: recarregarTurns } = useLive<AgentTurn[]>(
    `/leads/${leadId}/turns`,
    ["agent_turns"],
  );
  const [texto, setTexto] = useState("");
  /** Modelo, custo e ferramentas embaixo de cada resposta. Desligado por padrao. */
  const [detalhes, setDetalhes] = useState(false);
  const [aba, setAba] = useState<"conversa" | "rastro">("conversa");
  const [contaAberta, setContaAberta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  /*
   * Descartar apaga de verdade, entao passa por uma confirmacao que diz o que
   * some. Um `window.confirm` com "tem certeza?" nao informa nada -- quem
   * clica precisa ver quantas mensagens vao junto e decidir com isso na tela.
   */
  const [descartando, setDescartando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const fim = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages.length]);

  if (!data?.lead) return <Carregando linhas={4} />;
  const { lead, facts, messages, formulario } = data;
  const factByField = new Map(facts.map((f) => [f.field, f]));

  /*
   * Qual turno produziu cada resposta.
   *
   * Casado pelo texto do balao, que e exatamente o que o turno gravou como
   * `reply` -- nao ha coluna ligando mensagem a turno. Cada turno vale por uma
   * mensagem so, senao um "ok" repetido herdaria o turno do anterior.
   */
  const turnoPorMensagem = new Map<string, AgentTurn>();
  if (turns) {
    const usados = new Set<string>();
    for (const m of messages) {
      const corpo = m.body;
      if (m.author !== "ai" || !corpo) continue;
      const t = turns.find(
        (x) => !usados.has(x.id) && x.reply !== null && x.reply.includes(corpo),
      );
      if (t) {
        usados.add(t.id);
        turnoPorMensagem.set(m.id, t);
      }
    }
  }

  /*
   * Quem assume e quem esta logado -- o servidor resolve.
   *
   * Isto mandava o primeiro corretor da lista e, sem nenhum cadastrado, saia
   * calado antes de chamar a API: o botao nao fazia nada e nao dizia por que.
   * Agora nao manda corretor nenhum, e se falhar a tela mostra o erro.
   */
  async function assumir() {
    if (!lead.conversation_id) {
      setFalha("Este lead ainda não tem conversa — não há o que assumir.");
      return;
    }
    setFalha(null);
    try {
      await post(`/conversations/${lead.conversation_id}/claim`);
      reload();
    } catch (e) {
      setFalha(`Não deu para assumir: ${(e as Error).message}`);
    }
  }
  async function liberar() {
    if (!lead.conversation_id) return;
    setFalha(null);
    try {
      await post(`/conversations/${lead.conversation_id}/release`);
      reload();
    } catch (e) {
      setFalha(`Não deu para devolver: ${(e as Error).message}`);
    }
  }
  async function enviar() {
    if (!texto.trim() || !lead.conversation_id) return;
    setEnviando(true);
    try {
      await post(`/conversations/${lead.conversation_id}/send`, {
        text: texto,
      });
      setTexto("");
      reload();
    } finally {
      setEnviando(false);
    }
  }

  const podeEscrever = lead.control === "human";

  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-ink-3 hover:text-ink transition-colors"
      >
        <ArrowLeft size={15} />
        Voltar
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <span className="scale-125 origin-left">
            <Avatar nome={lead.name} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight">
                {lead.name ?? "Sem nome"}
              </h1>
              <StagePill stage={lead.stage} />
              <ControlPill control={lead.control} />
              {lead.opted_out && (
                <span className="inline-flex items-center gap-1.5">
                  <Pill tone="danger">pediu pra não receber</Pill>
                  {/* A detecção erra: "sair do aluguel" já virou opt-out. Sem
                      este botão, o engano é definitivo. */}
                  <button
                    onClick={async () => {
                      await post(`/leads/${leadId}/voltar-a-atender`);
                      reload();
                    }}
                    className="text-xs text-ink-3 hover:text-ink underline underline-offset-2"
                    title="Foi engano — a IA volta a responder esta pessoa"
                  >
                    voltar a atender
                  </button>
                </span>
              )}
            </div>
            <div className="text-sm text-ink-3 mt-1 flex flex-wrap gap-x-2">
              <span className="tabular-nums">{formatPhone(lead.phone_e164)}</span>
              <span>·</span>
              <span>veio de {lead.source.replace("_", " ")}</span>
              <span>·</span>
              <span>na etapa há {ago(lead.stage_since)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-44 space-y-1.5">
            <Score value={lead.fit_score} label="fit" />
            <Score value={lead.temp_score} label="temp" tone="warm" />
          </div>
          <div className="flex gap-2">
            {lead.control === "human" ? (
              <Button icone={Bot} onClick={liberar}>
                Devolver pra IA
              </Button>
            ) : (
              <Button variant="primary" icone={UserCheck} onClick={assumir}>
                Assumir conversa
              </Button>
            )}
            <CopiarConversa leadId={leadId} />
            <Button
              variant="ghost"
              icone={Trash2}
              onClick={() => setDescartando(true)}
              title="Descartar este lead"
              className="hover:!text-danger"
            />
          </div>
        </div>
      </div>

      {falha && (
        <p className="text-sm text-danger bg-danger-dim border border-danger/30 rounded-lg px-3.5 py-2.5">
          {falha}
        </p>
      )}

      {descartando && (
        <ConfirmarDescarte
          nome={lead.name}
          telefone={lead.phone_e164}
          mensagens={messages.length}
          onCancelar={() => setDescartando(false)}
          onFeito={onBack}
          leadId={leadId}
        />
      )}

      {formulario && <DoFormulario f={formulario} />}

      <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
        {/* conversa / rastro */}
        <div className="space-y-3">
          <Segmentos
            atual={aba}
            onChange={setAba}
            opcoes={[
              {
                id: "conversa",
                rotulo: (
                  <span className="flex items-center gap-1.5">
                    <MessageSquare size={14} /> Conversa
                  </span>
                ),
              },
              {
                id: "rastro",
                rotulo: (
                  <span className="flex items-center gap-1.5">
                    <Sparkles size={14} /> O que a IA fez
                  </span>
                ),
              },
            ]}
          />

          {aba === "rastro" ? (
            <div className="max-h-[65vh] overflow-y-auto pr-1">
              <Rastro turns={turns ?? null} onMudou={recarregarTurns} />
            </div>
          ) : (
        <Card className="flex flex-col h-[65vh] overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-rule text-xs">
            <span className="text-ink-3 tabular-nums">
              {messages.length} mensagem{messages.length === 1 ? "" : "s"}
            </span>
            <button
              onClick={() => setDetalhes((v) => !v)}
              className="text-ink-3 hover:text-ink transition-colors"
            >
              {detalhes ? "esconder detalhes" : "mostrar modelo e custo"}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.map((m) => {
              const meu = m.author !== "lead";
              return (
                <div
                  key={m.id}
                  className={`flex ${meu ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[76%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      meu
                        ? `rounded-br-md ${
                            m.status === "failed"
                              ? "bg-danger-dim text-ink border border-danger/40"
                              : m.status === "queued"
                                ? "bg-surface-2 text-ink border border-warm/40 border-dashed"
                                : "bg-accent/15 text-ink border border-accent/20"
                          }`
                        : "rounded-bl-md bg-surface-2 text-ink border border-rule"
                    }`}
                  >
                    <Midia m={m} />
                    {m.body ??
                      (m.tem_midia ? null : (
                        <span className="italic text-ink-3">({m.kind})</span>
                      ))}
                    <div className="text-[11px] text-ink-3 mt-1 flex items-center gap-1.5">
                      {/* O lead nunca ve essa distincao -- e so auditoria interna. */}
                      {m.author === "human" && <span className="text-accent">corretor</span>}
                      {m.author === "ai" && <span>IA</span>}
                      <span>{ago(m.created_at)}</span>
                      {/* Mensagem presa na fila tem que parecer diferente de
                          entregue: o cliente ainda nao recebeu nada. */}
                      {meu && m.status === "queued" && (
                        <span className="text-warm inline-flex items-center gap-1" title="ainda não saiu — na fila">
                          <Clock size={11} /> enviando
                        </span>
                      )}
                      {/* Os tiques do WhatsApp: saiu, chegou, foi lido. Uma
                          conversa inteira sem o segundo tique é o sinal de que
                          o número bloqueou a gente. */}
                      {meu && m.status === "sent" && (
                        <span title="enviada, ainda não chegou no celular">
                          <Check size={12} />
                        </span>
                      )}
                      {meu && m.status === "delivered" && (
                        <span title="entregue no celular">
                          <CheckCheck size={12} />
                        </span>
                      )}
                      {meu && m.status === "read" && (
                        <span title="visualizada" className="text-accent">
                          <CheckCheck size={12} />
                        </span>
                      )}
                      {meu && m.status === "failed" && (
                        <span
                          className="text-danger"
                          title={m.error ?? "o envio falhou"}
                        >
                          <CircleAlert size={11} className="inline -mt-px mr-0.5" /> não enviou
                        </span>
                      )}
                    </div>
                    {detalhes && turnoPorMensagem.has(m.id) && (
                      <MetaDoTurno t={turnoPorMensagem.get(m.id)!} />
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={fim} />
          </div>

          <div className="border-t border-rule p-3 bg-surface/80">
            {podeEscrever ? (
              <div className="flex gap-2">
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && void enviar()}
                  placeholder="Escreva pro cliente — sai com o mesmo nome de sempre"
                  className="flex-1 h-10 px-3.5 text-sm"
                />
                <Button
                  variant="primary"
                  icone={SendHorizontal}
                  onClick={enviar}
                  disabled={enviando || !texto.trim()}
                  className="h-10"
                >
                  Enviar
                </Button>
              </div>
            ) : (
              <p className="flex items-center justify-center gap-2 text-xs text-ink-3 py-1.5">
                <Bot size={14} className="text-accent" />
                A IA está atendendo. Assuma a conversa pra escrever.
              </p>
            )}
          </div>
        </Card>
          )}
        </div>

        {/* o que sabemos */}
        <div className="space-y-3">
          <Card className="p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
              <ClipboardList size={15} className="text-accent" />
              O que já descobrimos
            </h3>
            <div className="space-y-3.5">
              {FIELD_GROUPS.map((g) => {
                const preenchidos = g.fields.filter((f) => factByField.has(f));
                if (preenchidos.length === 0) return null;
                return (
                  <div key={g.title}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 mb-2">
                      {g.title}
                    </div>
                    <dl className="space-y-1.5">
                      {preenchidos.map((field) => {
                        const f = factByField.get(field)!;
                        return (
                          <div
                            key={field}
                            className="flex justify-between gap-2 text-sm items-baseline"
                          >
                            <dt className="text-ink-3 shrink-0">
                              {FIELD_LABEL[field] ?? field}
                            </dt>
                            <dd className="text-ink text-right min-w-0">
                              {formatFact(field, f.value)}
                              {f.origin === "inferido" && (
                                <span
                                  className="ml-1.5 align-middle"
                                  title="a IA deduziu — confirmar com o cliente"
                                >
                                  <Pill tone="warm">inferido</Pill>
                                </span>
                              )}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>
                );
              })}
              {facts.length === 0 && (
                <p className="text-sm text-ink-3">
                  Nada ainda. A conversa mal começou.
                </p>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <button
              onClick={() => setContaAberta((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-semibold text-ink hover:text-accent transition-colors"
            >
              <span className="flex items-center gap-2">
                <Calculator size={15} className="text-accent" />
                Por que o fit é {lead.fit_score}
              </span>
              {contaAberta ? (
                <ChevronDown size={16} className="text-ink-3" />
              ) : (
                <ChevronRight size={16} className="text-ink-3" />
              )}
            </button>
            {contaAberta && (
              <div className="space-y-1.5 mt-4">
                {data.fit
                  .filter((c) => c.ordem < 99)
                  .map((c) => (
                    <div key={c.ordem} className="flex items-baseline gap-2 text-xs">
                      <span
                        className={`tabular-nums w-9 shrink-0 ${
                          c.pontos > 0 ? "text-accent" : "text-ink-3"
                        }`}
                      >
                        {c.pontos}/{c.maximo}
                      </span>
                      <span className={c.pontos > 0 ? "text-ink-2" : "text-ink-3"}>
                        {c.componente}
                      </span>
                      <span
                        className={`ml-auto text-right truncate ${
                          c.pontos > 0 ? "text-ink" : "text-ink-3 italic"
                        }`}
                        title={c.detalhe}
                      >
                        {c.detalhe}
                      </span>
                    </div>
                  ))}
                {data.fit
                  .filter((c) => c.ordem === 99)
                  .map((c) => (
                    <p
                      key={c.ordem}
                      className="text-xs text-warm border-l-2 border-warm pl-2 mt-2 leading-snug"
                    >
                      {c.detalhe}
                    </p>
                  ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
              <History size={15} className="text-accent" />
              Linha do tempo
            </h3>
            <ol className="space-y-3 max-h-80 overflow-y-auto text-xs relative before:absolute before:left-[3px] before:top-1 before:bottom-1 before:w-px before:bg-rule-2">
              {data.timeline
                .filter((t) => t.kind !== "mensagem")
                .reverse()
                .map((t, i) => (
                  <li key={i} className="flex gap-3 relative">
                    <span
                      className={`mt-1 w-[7px] h-[7px] rounded-full shrink-0 ring-4 ring-surface ${
                        t.kind === "handoff"
                          ? "bg-warm"
                          : t.kind === "etapa"
                            ? "bg-accent"
                            : t.kind === "imovel"
                              ? "bg-ok"
                              : "bg-rule-2"
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="text-ink font-medium">{t.body}</div>
                      {t.detail && <div className="text-ink-3 mt-0.5 leading-relaxed">{t.detail}</div>}
                      <div className="text-ink-3 text-[11px]">{ago(t.at)}</div>
                    </div>
                  </li>
                ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * A confirmação de descarte.
 *
 * Descartar não é o mesmo que mover para "perdido", e a tela precisa dizer
 * isso: perdido é um desfecho — a pessoa existiu, foi atendida e não fechou, e
 * conta no relatório. Descartar é dizer que aquilo nunca foi um lead.
 *
 * A opção de ignorar o número junto está aqui porque é o caso comum: quem
 * descarta um "oi" de teste não quer ele de volta na próxima mensagem, e
 * obrigar a fazer isso em duas telas garante que metade das vezes só a
 * primeira é feita.
 */
const ROTULO_MODELO: Record<string, string> = {
  "claude-opus-5": "Opus 5",
  "claude-sonnet-5": "Sonnet 5",
};

/**
 * O que produziu aquela resposta, embaixo dela.
 *
 * Escondido por padrao, porque quem abre a conversa quer ler a conversa. Mas
 * quando a resposta sai estranha a primeira pergunta e sempre a mesma -- que
 * modelo era, e o que ele consultou antes de falar? -- e ate aqui isso exigia
 * trocar de aba e procurar o turno certo pelo horario.
 */
function MetaDoTurno({ t }: { t: AgentTurn }) {
  const ferramentas = t.steps
    .filter((s) => s.tipo === "ferramenta")
    .map((s) => (s.tipo === "ferramenta" ? s.nome : ""))
    .filter(Boolean);

  return (
    <div className="mt-1.5 pt-1.5 border-t border-rule/70 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-ink-3 tabular-nums">
      <span className="text-ink-2">
        {ROTULO_MODELO[t.model ?? ""] ?? t.model ?? "modelo desconhecido"}
      </span>
      {t.chamadas != null && (
        <span title="Idas à API: 1 + uma por rodada de ferramentas">
          {t.chamadas} {t.chamadas === 1 ? "chamada" : "chamadas"}
        </span>
      )}
      {t.duration_ms != null && <span>{(t.duration_ms / 1000).toFixed(1)}s</span>}
      {t.cost_usd > 0 && <span>US$ {Number(t.cost_usd).toFixed(4)}</span>}
      {ferramentas.length > 0 && (
        <span className="text-warm">{ferramentas.join(" · ")}</span>
      )}
      {t.ruim && <span className="text-danger">marcada como ruim</span>}
    </div>
  );
}

/**
 * A conversa inteira na área de transferência.
 *
 * Existe para poder colar em outro lugar -- inclusive num chat com uma IA -- e
 * pedir uma análise do que aconteceu. O texto vem pronto do servidor e junta o
 * que hoje está espalhado por seis telas: mensagens com recibo, turno com
 * modelo e ferramentas, fatos, formulário, follow-ups e agendamentos.
 *
 * `navigator.clipboard` só existe em contexto seguro. Quando não dá, a saída
 * não pode ser um erro seco: abre o texto numa caixa já selecionada, que é o
 * que permite copiar na mão.
 */
function CopiarConversa({ leadId }: { leadId: string }) {
  const [estado, setEstado] = useState<"parado" | "indo" | "copiado">("parado");
  const [texto, setTexto] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function copiar() {
    setEstado("indo");
    setErro(null);
    try {
      const r = await get<{ texto: string }>(`/leads/${leadId}/exportar`);
      try {
        await navigator.clipboard.writeText(r.texto);
        setEstado("copiado");
        setTimeout(() => setEstado("parado"), 2000);
      } catch {
        // Sem permissão de área de transferência: mostra para copiar na mão.
        setTexto(r.texto);
        setEstado("parado");
      }
    } catch (e) {
      setErro((e as Error).message);
      setEstado("parado");
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        icone={estado === "copiado" ? Check : Copy}
        onClick={copiar}
        disabled={estado === "indo"}
        title="Copia a conversa inteira, com modelo, custo, ferramentas e recibos"
      >
        {estado === "copiado" ? "copiado" : estado === "indo" ? "copiando…" : "Copiar"}
      </Button>

      {erro && <span className="text-xs text-danger self-center">{erro}</span>}

      {texto !== null && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          onClick={() => setTexto(null)}
        >
          <div
            className="w-full max-w-3xl rounded-xl bg-surface border border-rule shadow-pop p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-ink">
                Copie o texto abaixo
              </h3>
              <Button
                variant="ghost"
                icone={X}
                onClick={() => setTexto(null)}
                title="Fechar"
              />
            </div>
            <p className="text-xs text-ink-3">
              O navegador não deixou copiar sozinho. Está tudo selecionado —
              use Ctrl+C.
            </p>
            <textarea
              autoFocus
              readOnly
              value={texto}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full h-[55vh] font-mono text-xs p-3 leading-relaxed"
            />
          </div>
        </div>
      )}
    </>
  );
}

function ConfirmarDescarte({
  leadId,
  nome,
  telefone,
  mensagens,
  onCancelar,
  onFeito,
}: {
  leadId: string;
  nome: string | null;
  telefone: string;
  mensagens: number;
  onCancelar: () => void;
  onFeito: () => void;
}) {
  const [ignorar, setIgnorar] = useState(false);
  const [indo, setIndo] = useState(false);

  return (
    <Card className="p-5" stripe="danger">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <Trash2 size={15} className="text-danger" />
        Descartar {nome ?? formatPhone(telefone)}?
      </div>
      <p className="text-sm text-ink-3 mt-2 max-w-prose">
        Some o lead e tudo que pendura nele: {mensagens} mensagem
        {mensagens === 1 ? "" : "s"}, os fatos descobertos, o histórico do
        agente e qualquer agendamento. Não dá para desfazer.
      </p>
      <p className="text-sm text-ink-3 mt-2 max-w-prose">
        Se a pessoa é real e só não fechou, mova para <em>Perdido</em> no funil
        — assim ela continua contando no relatório.
      </p>

      <label className="flex items-center gap-2 mt-3 text-sm text-ink-2">
        <input
          type="checkbox"
          checked={ignorar}
          onChange={(e) => setIgnorar(e.target.checked)}
        />
        Ignorar {formatPhone(telefone)} daqui pra frente
      </label>

      <div className="flex gap-2 mt-4">
        <Button
          variant="danger"
          icone={Trash2}
          disabled={indo}
          onClick={async () => {
            setIndo(true);
            await del(`/leads/${leadId}${ignorar ? "?ignorar=1" : ""}`);
            onFeito();
          }}
        >
          Descartar
        </Button>
        <Button variant="ghost" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </Card>
  );
}

/**
 * O que o site mandou, cru.
 *
 * O painel escondia dado que já tinha. Os campos conhecidos do formulário
 * viram fatos; a mensagem que a pessoa escreveu e os campos que não mapeiam
 * para nada nosso ficavam só no banco. A ficha dizia "Nada ainda, a conversa
 * mal começou" enquanto o texto em que ela explicou o que queria estava
 * guardado a uma consulta de distância.
 *
 * Importa mais no lead que ficou parado: é o único contexto que existe antes
 * de alguém decidir resgatar aquele contato.
 */
function DoFormulario({ f }: { f: FormularioRecebido }) {
  const campos = Object.entries(f.payload).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== "",
  );
  if (campos.length === 0) return null;

  // A mensagem livre vem primeiro e com destaque: é o que a pessoa escolheu
  // escrever, e vale mais que qualquer campo de select do formulário.
  const msg = campos.find(([k]) => k.toLowerCase() === "mensagem");
  const resto = campos.filter(([k]) => k.toLowerCase() !== "mensagem");

  return (
    <Card className="p-5" stripe="accent">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <FileText size={15} className="text-accent" />
        Veio do formulário
        <span className="font-normal text-ink-3">· {f.origem}</span>
      </div>

      {msg && (
        <p className="text-sm text-ink mt-3 whitespace-pre-wrap px-3.5 py-2.5 rounded-lg bg-surface-2 border border-rule">
          “{String(msg[1])}”
        </p>
      )}

      {resto.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
          {resto.map(([k, v]) => (
            <span key={k} className="text-sm">
              <span className="text-ink-3">{k.replace(/_/g, " ")}: </span>
              <span className="text-ink-2">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * O que o lead mandou, do jeito que ele mandou.
 *
 * Antes o painel mostrava "(image)" e pronto: quem atendia sabia que chegou
 * algo e não tinha como ver. Foto de lead costuma ser o print do anúncio, a
 * planta que ele achou ou um comprovante — e perguntar "o que você mandou?" é
 * a pior resposta possível.
 *
 * Áudio aparece com player porque a transcrição não é o áudio: ela erra nome
 * próprio, número e ironia, e a decisão da conversa às vezes está no tom.
 */
function Midia({ m }: { m: Message }) {
  if (!m.tem_midia) return null;
  const url = `/api/mensagens/${m.id}/midia`;

  if (m.kind === "image" || m.kind === "sticker") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mb-1.5">
        <img
          src={url}
          alt="imagem enviada pelo lead"
          className="rounded-lg max-h-72 w-auto border border-rule"
        />
      </a>
    );
  }

  if (m.kind === "audio") {
    return (
      <div className="mb-1.5 space-y-1">
        <audio controls preload="none" src={url} className="w-full max-w-72 h-9" />
        {m.body && (
          <p className="text-[11px] text-ink-3">
            transcrição automática — o áudio acima é o que ela disse de verdade
          </p>
        )}
      </div>
    );
  }

  if (m.kind === "video") {
    return (
      <video
        controls
        preload="none"
        src={url}
        className="rounded-lg max-h-72 w-auto mb-1.5 border border-rule"
      />
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 mb-1.5 text-accent hover:underline"
    >
      <Paperclip size={13} />
      abrir arquivo
    </a>
  );
}
